'use client';

import { ISalePageCommentResponse } from '@/api/types';
import { SalePageCommentCommentUpdateInput } from '@/app/sales/components/commentInput/UpdateCommentInput';
import { ProfileImage, Tag } from '@/components';
import { useSalePageQuery } from '@/entities/salePage';
import { Column, Element, Ellipsis, Icon, Row } from '@vuddy/components';
import { getRelativeTime, isNonNil, trackEvent } from '@vuddy/utils';
import { isEmpty } from 'lodash-es';
import { useState } from 'react';
import { SalePageCommentMenu } from '../commentMenu';
import * as styles from './comment.css';

interface SalePageCommentProps {
  comment: ISalePageCommentResponse;
  salePageId: number;
  interactive?: boolean;
}

export const SalePageComment = ({
  comment,
  salePageId,
  interactive = true,
}: SalePageCommentProps) => {
  const [isEditMode, setIsEditMode] = useState(false);
  const isHidden = isNonNil(comment.hiddenAt);
  const isDeletedAuthor = comment.author.userId === -1 && !isHidden;
  const isOptimistic = comment.id === -1;
  const showMenuButton = !isOptimistic && !isHidden;
  const salePageQuery = useSalePageQuery(salePageId);

  const exitEditMode = () => {
    setIsEditMode(false);
    trackEvent('shop', 'click_sale_comment_delete', {
      channel_id: salePageQuery.data?.channelId,
      sale_type: salePageQuery.data?.type,
    });
  };

  const handleEditMenuSelect = () => {
    setIsEditMode(true);
    trackEvent('shop', 'click_sale_comment_modification', {
      channel_id: salePageQuery.data?.channelId,
      sale_type: salePageQuery.data?.type,
    });
  };

  return isEditMode ? (
    <SalePageCommentCommentUpdateInput
      comment={comment}
      salePageId={salePageId}
      exitEditMode={exitEditMode}
    />
  ) : (
    <div className={styles.wrapper({ isOptimistic, isHidden })}>
      <ProfileImage
        url={isHidden ? undefined : comment.author.profileImage?.url}
        imageSize="xs"
      />
      {isHidden ? (
        <Element typography="bodyXs" color="tertiary">
          신고 누적으로 숨기기 처리 되었습니다.
        </Element>
      ) : (
        <>
          <Column gap="gap_xs" flex={1}>
            <Row alignItems="center" gap="gap_xs">
              <Ellipsis typography="bodyXsAccent" height={20}>
                {isDeletedAuthor
                  ? '(탈퇴한 사용자)'
                  : isEmpty(comment.author.name)
                    ? ' '
                    : comment.author.name}
              </Ellipsis>
              {comment.author.type === 'CHANNEL' && (
                <Icon name="FILL_VERIFIED" size="16px" color="primary" />
              )}
              {comment.author.type === 'SECONDARY_CHANNEL' && (
                <Icon name="FILL_FAN_CREATOR" size="16px" color="primary" />
              )}
              {comment.author.isBuyer && (
                <Tag label="구매자" variant="lightblue" size="small" />
              )}
              <Element typography="captionBase" color="tertiary" flex="none">
                {getRelativeTime(comment.createdAt)}
              </Element>
            </Row>
            <Element className={styles.content}>{comment.content}</Element>
          </Column>
          {showMenuButton && (
            <SalePageCommentMenu
              comment={comment}
              interactive={interactive}
              salePageId={salePageId}
              onEditMenuSelect={handleEditMenuSelect}
            />
          )}
        </>
      )}
    </div>
  );
};
